import React, { useEffect, useRef, useState } from 'react';
import SavedBranding from './SavedBranding';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowDownToLine,
  CaseUpper,
  Copy,
  Eraser,
  Gauge,
  Italic,
  Pipette,
  Undo2,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpFromLine,
  Ban,
  Blend,
  ChevronsRight,
  Maximize2,
  Mic,
  MoveRight,
  Music,
  PanelLeftOpen,
  Play,
  Plus,
  RefreshCw,
  RotateCw,
  Type,
  Upload,
  Volume1,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import {
  addAudio,
  addText,
  createEditorId,
  layOutClips,
  resolveTextStyle,
  MAX_TEXT_WIDTH,
  MIN_TEXT_WIDTH,
  TEXT_FONTS,
  TEXT_PRESETS,
  TEXT_SHADOW,
  TEXT_WEIGHTS,
  type TextFont,
  type TextPreset,
  overlayMotion,
  plateLogoMotion,
  MAX_MOTION_SECONDS,
  MIN_MOTION_SECONDS,
  MOTION_PERSONALITIES,
  MOTION_STYLES,
  type ElementMotion,
  type MotionDirection,
  type MotionPersonality,
  type MotionStyle,
  setAllClipAudio,
  copyPlateStyle,
  clampAudioFades,
  MAX_AUDIO_FADE_SECONDS,
  setClipAudio,
  DEFAULT_VOICE_DUCK,
  setClipFit,
  setClipOffset,
  setClipScale,
  clipScale,
  clipSourceEnd,
  insertSpeedZone,
  normaliseSpeedZones,
  roundTime,
  setClipSpeed,
  setClipSpeedZones,
  speedOutputSeconds,
  speedSourceAt,
  CLIP_SPEED_PRESETS,
  MIN_SPEED_ZONE_SECONDS,
  MIN_CLIP_SCALE,
  insertBlank,
  setClipTransition,
  clipGap,
  closeClipGap,
  updateBlank,
  setClipTrim,
  updateAudio,
  updateLogo,
  updateText,
  DEFAULT_PLATE_LOGO_SCALE,
  MAX_OVERLAY_LOGO_SCALE,
  MAX_PLATE_LOGO_SCALE,
  MIN_OVERLAY_LOGO_SCALE,
  MIN_PLATE_LOGO_SCALE,
  deleteAudio,
  deleteText,
  visualDuration,
  isAnimatedScene,
  stillAssetId,
  type EditorAudio,
  type EditorClip,
  type EditorState,
  type EditorText,
} from '@rehub/propertycut-timeline';
import { LibraryTrackList } from '@rehub/branded-viewer';
import ClipGradePanel, { type SampledFrame } from './ClipGradePanel';
import { supabase } from '../../services/supabase';
import type { AgentData, ListingData, PropertyCutAsset, PropertyCutMusicTrack, StartWorkflow } from '../../types';
import { removeLogoBackground, sampleLogoBackground } from '../../services/logoBackground';
import { CLIP_TRANSITIONS, shownTransition, type EditorSelection } from './model';
import { AnimateAllPhotos, FootageVersionSwitch, SceneMotionPanel, WalkthroughCard } from './ScenePanels';
import VoiceoverComposer from './VoiceoverComposer';
import type { SceneTools } from './useSceneJobs';

export type Tab = 'media' | 'text' | 'audio' | 'logo';

export interface UploadJob {
  id: string;
  name: string;
  status: 'waiting' | 'uploading' | 'done' | 'error';
  fraction: number;
}

interface SidePanelProps {
  tab: Tab;
  onTab: (tab: Tab) => void;
  state: EditorState;
  assets: PropertyCutAsset[];
  music: PropertyCutMusicTrack[];
  musicPreviewUrls?: Record<string, string>;
  listing: ListingData;
  projectId: string;
  agent: AgentData;
  selection: EditorSelection;
  uploadError: string | null;
  uploading: boolean;
  uploads: UploadJob[];
  onBrowse: () => void;
  onBrowseVoiceover: () => void;
  onVoiceoverReady: (asset: PropertyCutAsset) => void;
  onDropFiles: (files: File[]) => void;
  onAddAsset: (asset: PropertyCutAsset) => void;
  onRemoveAsset: (asset: PropertyCutAsset) => void;
  onEdit: (recipe: (state: EditorState) => EditorState) => void;
  /** Drag preview. `commit` writes one undo step. */
  onLiveEdit: (recipe: (state: EditorState) => EditorState, commit: boolean) => void;
  onSampleClip: (clipId: string) => Promise<SampledFrame>;
  neutralPickClipId: string | null;
  onNeutralPick: (clipId: string | null) => void;
  gradeNote: string | null;
  onGradeNote: (note: string | null) => void;
  onListing: (listing: ListingData) => void;
  onAgent: (agent: AgentData) => void;
  onUploadLogo: (file: File) => Promise<void>;
  onSelect: (selection: EditorSelection) => void;
  /** Current playhead, in timeline seconds. */
  playhead: number;
  /** Seek to `time` and play, to watch an animation. Pauses at `until` when given. */
  onPlayFrom: (time: number, until?: number) => void;
  scenes: SceneTools;
  workflow: StartWorkflow | null;
  userId: string;
  /** When false, the panel is hidden below the `md` breakpoint and shown beside the stage on desktop. */
  mobileVisible?: boolean;
}

export default function SidePanel(props: SidePanelProps) {
  const inspectorRef = useRef<HTMLDivElement>(null);
  const selectionKey = props.selection
    ? `${props.selection.type}:${'id' in props.selection ? props.selection.id : ''}`
    : null;
  useEffect(() => {
    if (selectionKey) inspectorRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [selectionKey]);
  return (
    <aside className={`${props.mobileVisible === false ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 md:shrink-0 flex-1 md:flex-none min-h-0 min-w-0 md:border-r border-slate-800 bg-slate-950`}>
      <div className="grid grid-cols-4 border-b border-slate-800">
        {(['media', 'text', 'audio', 'logo'] as Tab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => props.onTab(tab)}
            className={`py-3 px-1 text-[11px] sm:text-xs uppercase tracking-wide sm:tracking-wider ${
              props.tab === tab ? 'text-teal-200 border-b-2 border-teal-400' : 'text-slate-500'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {props.tab === 'media' && <MediaTab {...props} />}
        {props.tab === 'text' && <TextTab {...props} />}
        {props.tab === 'audio' && <AudioTab {...props} />}
        {props.tab === 'logo' && <LogoTab {...props} />}
        <div ref={inspectorRef} className="scroll-mt-3">
          <Inspector {...props} />
        </div>
      </div>
    </aside>
  );
}

function MediaTab(props: SidePanelProps) {
  const visuals = props.assets.filter((asset) => asset.kind !== 'audio');
  const total = props.uploads.length;
  const finished = props.uploads.filter((job) => job.status === 'done').length;
  const overall = total
    ? props.uploads.reduce((sum, job) => sum + (job.status === 'done' ? 1 : job.fraction), 0) / total
    : 0;
  const active = props.uploads.find((job) => job.status === 'uploading');
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={props.onBrowse}
        disabled={props.uploading}
        className="w-full rounded-lg border border-dashed border-slate-600 hover:border-teal-400/70 py-6 px-3 text-sm text-slate-300"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (props.uploading) return;
          props.onDropFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <Upload className="w-4 h-4 mx-auto mb-2 text-teal-300" />
        {props.uploading
          ? `Uploading ${Math.min(finished + 1, total)} of ${total}`
          : 'Upload photos or video'}
        {active && <span className="block mt-1 text-[11px] text-slate-500 truncate">{active.name}</span>}
        {total > 0 && (
          <span className="mt-3 block h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <span
              className="block h-full bg-teal-400 transition-[width] duration-150"
              style={{ width: `${Math.round(overall * 100)}%` }}
            />
          </span>
        )}
      </button>
      {total > 0 && (
        <ul className="space-y-2">
          {props.uploads.map((job) => (
            <li key={job.id}>
              <div className="flex items-center justify-between gap-2 text-[11px] text-slate-400">
                <span className="truncate">{job.name}</span>
                <span className="shrink-0 tabular-nums">
                  {job.status === 'done' ? 'Done' : job.status === 'error' ? 'Failed' : job.status === 'waiting' ? 'Waiting' : `${Math.round(job.fraction * 100)}%`}
                </span>
              </div>
              <div className="mt-1 h-1 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className={`h-full ${job.status === 'error' ? 'bg-amber-400' : 'bg-teal-400/80'}`}
                  style={{ width: `${Math.round((job.status === 'done' ? 1 : job.fraction) * 100)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      {props.workflow === 'slideshow' && (
        <p className="text-[11px] text-slate-500">
          Each photo holds for 5 seconds and stays completely still. Change the length or framing by selecting a photo on the timeline.
        </p>
      )}
      {props.workflow === 'standard' ? null : props.workflow === 'walkthrough' ? (
        <>
          <WalkthroughCard tools={props.scenes} defaultOpen />
          <AnimateAllPhotos tools={props.scenes} />
        </>
      ) : (
        <>
          <AnimateAllPhotos tools={props.scenes} />
          <WalkthroughCard tools={props.scenes} defaultOpen={false} />
        </>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => {
            let id = '';
            props.onEdit((state) => {
              const next = insertBlank(state, 'start', { color: props.agent.brand_color });
              id = next.clips[0]?.id ?? '';
              return next;
            });
            if (id) props.onSelect({ type: 'clip', id });
          }}
          className="rounded-md border border-slate-700 py-2 text-xs text-slate-200 hover:border-teal-400/60"
        >
          Opening
        </button>
        <button
          type="button"
          onClick={() => {
            let id = '';
            props.onEdit((state) => {
              const next = insertBlank(state, 'end', { color: props.agent.brand_color });
              id = next.clips[next.clips.length - 1]?.id ?? '';
              return next;
            });
            if (id) props.onSelect({ type: 'clip', id });
          }}
          className="rounded-md border border-slate-700 py-2 text-xs text-slate-200 hover:border-teal-400/60"
        >
          End
        </button>
      </div>
      <p className="text-[11px] text-slate-500">A coloured plate for a logo reveal. In the preview, drag the logo’s corner to change its size.</p>
      <SavedBranding
        userId={props.userId}
        state={props.state}
        agent={props.agent}
        onEdit={props.onEdit}
        onAgent={props.onAgent}
        onSelect={props.onSelect}
      />
      {props.uploadError && <p className="text-xs text-amber-200">{props.uploadError}</p>}
      <ul className="space-y-2">
        {visuals.map((asset) => (
          <li key={asset.id} className="flex items-center gap-2 rounded-md bg-slate-900 border border-slate-800 p-2">
            <div className="w-12 h-9 rounded bg-slate-800 overflow-hidden shrink-0">
              {asset.kind !== 'video' && asset.signed_url ? (
                <img src={asset.signed_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-[10px] text-slate-500">Video</div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs truncate">{asset.original_filename || asset.kind}</p>
              {asset.source === 'imagemotion' && <p className="text-[10px] text-teal-300/80">ImageMotion animation</p>}
              {asset.source === 'videomotion' && <p className="text-[10px] text-teal-300/80">VideoMotion clip</p>}
              {asset.moderation_status === 'pending' && <p className="text-[10px] text-amber-300/80">Checking content…</p>}
              {asset.moderation_status === 'rejected' && <p className="text-[10px] text-red-300">Failed content check: remove this file</p>}
            </div>
            <button type="button" title="Add to timeline" aria-label="Add to timeline" onClick={() => props.onAddAsset(asset)} className="p-2 text-slate-300 hover:text-white">
              <Plus className="w-4 h-4" />
            </button>
            <button type="button" title="Remove" aria-label="Remove" onClick={() => props.onRemoveAsset(asset)} className="p-2 text-slate-500 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TextTab(props: SidePanelProps) {
  const duration = visualDuration(props.state) || 3;
  const add = (partial: Partial<EditorText> & Pick<EditorText, 'text'>) => {
    const next = addText(props.state, { duration: Math.min(4, duration), ...partial });
    props.onEdit(() => next);
    props.onSelect({ type: 'text', id: next.texts[next.texts.length - 1].id });
  };
  const addPreset = (preset: TextPreset, text = preset.text) => {
    const atEnd = preset.role === 'endcard';
    const length = Math.min(atEnd ? 3 : 4, duration);
    add({
      text,
      role: preset.role,
      fontSize: preset.fontSize,
      y: preset.y,
      background: preset.background,
      align: preset.align ?? 'center',
      start: atEnd ? Math.max(0, duration - length) : 0,
      duration: length,
      ...preset.style,
    });
  };
  const preset = (id: string) => TEXT_PRESETS.find((p) => p.id === id)!;
  const listing = props.listing;
  const stats = [
    listing.beds != null ? `${listing.beds} bed${listing.beds === 1 ? '' : 's'}` : '',
    listing.baths != null ? `${listing.baths} bath${listing.baths === 1 ? '' : 's'}` : '',
    listing.tenure ?? '',
  ].filter(Boolean).join(' · ');
  const agentLine = [props.agent.name, props.agent.phone, props.agent.email].map((s) => s?.trim()).filter(Boolean).join(' · ');
  const details: Array<{ label: string; run: () => void }> = [
    { label: 'Address', run: () => addPreset(preset('heading'), listing.address?.trim() || '1 High Street, Town') },
    {
      label: 'Price',
      run: () => add({
        text: listing.price?.trim() || '£500,000',
        role: 'price',
        fontSize: 48,
        y: 0.72,
        background: 'rgba(0,0,0,0.6)',
        fontWeight: 700,
      }),
    },
    { label: 'Beds & baths', run: () => addPreset(preset('subheading'), stats || '3 beds · 2 baths') },
    {
      label: 'Agent contact',
      run: () => {
        const length = Math.min(3, duration);
        add({
          text: agentLine || 'Agent name · 01234 567890',
          role: 'agent',
          fontSize: 30,
          y: 0.84,
          background: 'rgba(0,0,0,0.45)',
          start: Math.max(0, duration - length),
          duration: length,
        });
      },
    },
  ];
  const selectedId = props.selection?.type === 'text' ? props.selection.id : null;

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => add({ text: 'Your text', role: 'free', background: null, shadow: true, fontSize: 44 })}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-teal-400 py-2.5 text-sm font-semibold text-slate-950 hover:bg-teal-300"
      >
        <Type className="w-4 h-4" />
        Add your text
      </button>

      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">Text styles</p>
        {TEXT_PRESETS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => addPreset(item)}
            className="w-full rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-left text-slate-100 hover:border-teal-400/60"
          >
            <span
              className="block truncate"
              style={{
                fontSize: `${Math.max(12, Math.min(26, item.fontSize * 0.34))}px`,
                fontWeight: item.style.fontWeight ?? 500,
                letterSpacing: `${item.style.letterSpacing ?? 0}em`,
                textTransform: item.style.uppercase ? 'uppercase' : 'none',
              }}
            >
              {item.background ? (
                <span className="rounded px-1.5 py-0.5" style={{ background: item.background }}>{item.label}</span>
              ) : (
                item.label
              )}
            </span>
          </button>
        ))}
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">Property details</p>
        <div className="grid grid-cols-2 gap-1.5">
          {details.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={item.run}
              className="rounded-md border border-slate-700 py-2 text-xs text-slate-200 hover:border-teal-400/60"
            >
              {item.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">Uses the property details below when they are filled in.</p>
      </div>

      {props.state.texts.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">On the video</p>
          <ul className="space-y-1">
            {props.state.texts.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => props.onSelect({ type: 'text', id: item.id })}
                  className={`w-full flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs ${
                    selectedId === item.id ? 'border-teal-400 text-teal-100' : 'border-slate-800 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <span className="truncate flex-1">{item.text.trim() || 'Empty text'}</span>
                  <span className="shrink-0 tabular-nums text-slate-500">{item.start.toFixed(1)}s</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <OptionalDetails {...props} />
    </div>
  );
}

function OptionalDetails(props: SidePanelProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-800">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full text-left px-3 py-2 text-xs text-slate-300">
        Property details <span className="text-slate-500">optional</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <Field label="Address" value={props.listing.address ?? ''} onChange={(address) => props.onListing({ ...props.listing, address })} />
          <Field label="Price" value={props.listing.price ?? ''} onChange={(price) => props.onListing({ ...props.listing, price })} />
          <Field label="Agent" value={props.agent.name ?? ''} onChange={(name) => props.onAgent({ ...props.agent, name })} />
          <Field label="Phone" value={props.agent.phone ?? ''} onChange={(phone) => props.onAgent({ ...props.agent, phone })} />
          <p className="text-[11px] text-slate-500">Empty fields stay off the video. Add a title or caption above if you want words on screen.</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-[11px] text-slate-500">
      {label}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm text-white"
      />
    </label>
  );
}

function AudioTab(props: SidePanelProps) {
  const uploads = props.assets.filter((asset) => asset.kind === 'audio');
  const voiceIds = new Set(props.state.audios.filter((a) => a.role === 'voiceover').map((a) => a.assetId));
  const videoClips = props.state.clips.filter((clip) => {
    if (clip.blank) return false;
    return props.assets.find((asset) => asset.id === clip.assetId)?.kind === 'video';
  });
  const allMuted = videoClips.length > 0 && videoClips.every((clip) => clip.muted);
  const typical = videoClips.length
    ? videoClips.reduce((sum, clip) => sum + clip.volume, 0) / videoClips.length
    : 1;
  const duck = props.state.voiceDuck ?? DEFAULT_VOICE_DUCK;
  const hasVoice = props.state.audios.some((a) => a.role === 'voiceover');
  return (
    <div className="space-y-5">
      {props.uploadError && props.tab === 'audio' && <p className="text-xs text-amber-200">{props.uploadError}</p>}

      <section className="space-y-2">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">Voiceover</p>
        <VoiceoverComposer
          projectId={props.projectId}
          listing={props.listing}
          disabled={props.uploading}
          onReady={props.onVoiceoverReady}
        />
        <button
          type="button"
          onClick={props.onBrowseVoiceover}
          disabled={props.uploading}
          className="w-full rounded-lg border border-dashed border-slate-600 hover:border-teal-400/70 py-4 text-sm text-slate-300 disabled:opacity-50"
        >
          <Mic className="w-4 h-4 mx-auto mb-1 text-teal-300" />
          Upload voiceover
        </button>
        <p className="text-[11px] text-slate-500">Plays on its own track. Music and clip sound turn down while it speaks.</p>
        {hasVoice && (
          <VolumeSlider
            label="Music and clip sound under the voiceover"
            value={duck}
            onChange={(voiceDuck) => props.onEdit((state) => ({ ...state, voiceDuck }))}
          />
        )}
      </section>

      <section className="space-y-2">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">Music</p>
        <button type="button" onClick={props.onBrowse} className="w-full rounded-lg border border-dashed border-slate-600 hover:border-teal-400/70 py-3 text-sm text-slate-300">
          <Music className="w-4 h-4 mx-auto mb-1 text-teal-300" />
          Upload music
        </button>
        {uploads.map((asset) => (
          <div key={asset.id} className="flex items-center gap-2 text-xs">
            <span className="truncate flex-1">{asset.original_filename || 'Audio'}</span>
            {voiceIds.has(asset.id) && <span className="text-[10px] text-teal-300">Voiceover</span>}
            <button type="button" onClick={() => props.onAddAsset(asset)} className="text-teal-300">Add</button>
            <button type="button" onClick={() => props.onRemoveAsset(asset)} className="text-slate-500">Remove</button>
          </div>
        ))}
        {props.music.length === 0 && (
          <p className="text-[11px] text-slate-500">Royalty-free tracks will show here once they’re added.</p>
        )}
        <LibraryTrackList
          tracks={props.music.map((track) => ({
            id: track.id,
            title: track.title,
            durationSeconds: track.duration_seconds,
            storagePath: track.storage_path,
            previewUrl: props.musicPreviewUrls?.[track.id] ?? null,
          }))}
          onAdd={(track) => props.onEdit((state) => addAudio(state, { musicId: track.id, durationSeconds: track.durationSeconds }))}
          supabase={supabase}
        />
      </section>

      {videoClips.length > 0 && (
        <section className="space-y-2">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Sound from video clips</p>
          <VolumeSlider
            label={`All ${videoClips.length} clip${videoClips.length === 1 ? '' : 's'}`}
            value={allMuted ? 0 : typical}
            onChange={(volume) => props.onEdit((state) => setAllClipAudio(state, volume, volume <= 0))}
          />
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={allMuted}
              onChange={(e) => props.onEdit((state) => setAllClipAudio(state, e.target.checked ? typical : Math.max(typical, 0.5), e.target.checked))}
            />
            Mute every clip
          </label>
          <p className="text-[11px] text-slate-500">Select a clip on the timeline to set its own level.</p>
        </section>
      )}
    </div>
  );
}

function VolumeSlider({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  const Icon = value <= 0.001 ? VolumeX : value < 0.5 ? Volume1 : Volume2;
  return (
    <label className="block text-[11px] text-slate-500">
      <span className="flex items-center justify-between">
        {label}
        <span className="tabular-nums text-slate-300">{Math.round(value * 100)}%</span>
      </span>
      <span className="mt-1 flex items-center gap-2">
        <Icon className="w-4 h-4 shrink-0 text-slate-400" />
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-teal-400"
          aria-label={label}
        />
      </span>
    </label>
  );
}

function LogoPicker({
  agent,
  onAgent,
  onUploadLogo,
  swatch,
}: {
  agent: AgentData;
  onAgent: (agent: AgentData) => void;
  onUploadLogo: (file: File) => Promise<void>;
  swatch?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [beforeCutout, setBeforeCutout] = useState<string | null>(null);
  const background = swatch && /^#[0-9a-fA-F]{6}$/.test(swatch) ? swatch : '#0f172a';

  const chooseLogo = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLogoError('Choose a PNG, JPG, or WebP.');
      return;
    }
    setLogoError(null);
    setBeforeCutout(null);
    setUploading(true);
    try {
      await onUploadLogo(file);
    } catch (err: any) {
      setLogoError(err?.message || 'Could not upload the logo.');
    } finally {
      setUploading(false);
    }
  };

  const removeBackground = async () => {
    const original = agent.logo_url;
    if (!original) return;
    setLogoError(null);
    setRemoving(true);
    try {
      const cutout = await removeLogoBackground(original);
      if (!cutout) {
        setLogoError('This logo has no solid background to remove.');
        return;
      }
      await onUploadLogo(new File([cutout], 'logo-transparent.png', { type: 'image/png' }));
      setBeforeCutout(original);
    } catch (err: any) {
      setLogoError(err?.message || 'Could not remove the background.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-2">
      {agent.logo_url && (
        <div className="flex items-center gap-2">
          <div className="h-12 w-16 rounded border border-slate-700 flex items-center justify-center overflow-hidden" style={{ backgroundColor: background }}>
            <img src={agent.logo_url} alt="Uploaded logo" className="max-h-10 max-w-[3.5rem] object-contain" />
          </div>
          <div className="flex flex-col items-start gap-1">
            {beforeCutout ? (
              <button
                type="button"
                onClick={() => {
                  onAgent({ ...agent, logo_url: beforeCutout });
                  setBeforeCutout(null);
                }}
                className="flex items-center gap-1 text-xs text-teal-300 hover:text-teal-200"
              >
                <Undo2 className="w-3 h-3" />
                Restore background
              </button>
            ) : (
              <button
                type="button"
                disabled={removing || uploading}
                onClick={() => void removeBackground()}
                className="flex items-center gap-1 text-xs text-teal-300 hover:text-teal-200 disabled:text-slate-500"
              >
                <Eraser className="w-3 h-3" />
                {removing ? 'Removing background…' : 'Remove background'}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                onAgent({ ...agent, logo_url: undefined });
                setBeforeCutout(null);
              }}
              className="text-xs text-slate-400 hover:text-red-300"
            >
              Remove logo
            </button>
          </div>
        </div>
      )}
      <label className={`flex items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2.5 text-xs cursor-pointer ${uploading ? 'border-slate-700 text-slate-500' : 'border-slate-600 text-slate-200 hover:border-teal-400/70'}`}>
        <Upload className="w-3.5 h-3.5 text-teal-300" />
        {uploading ? 'Uploading logo…' : agent.logo_url ? 'Replace logo' : 'Upload logo'}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          disabled={uploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            void chooseLogo(file);
          }}
        />
      </label>
      {logoError && <p className="text-xs text-amber-200">{logoError}</p>}
    </div>
  );
}

function plateColor(value: string | undefined): string {
  return /^#[0-9a-fA-F]{6}$/.test(value ?? '') ? (value as string) : '#0f172a';
}

const PLATE_SWATCHES = ['#0f172a', '#000000', '#ffffff', '#f5f0e6', '#1e3a5f', '#14532d', '#7f1d1d'];

function PlateBackground({
  color,
  brandColor,
  logoUrl,
  onChange,
}: {
  color: string;
  brandColor?: string;
  logoUrl?: string;
  onChange: (color: string) => void;
}) {
  const [matching, setMatching] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const brand = brandColor && /^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor.toLowerCase() : null;
  const swatches = brand && !PLATE_SWATCHES.includes(brand) ? [brand, ...PLATE_SWATCHES] : PLATE_SWATCHES;

  const matchLogo = async () => {
    if (!logoUrl) return;
    setNote(null);
    setMatching(true);
    try {
      const sampled = await sampleLogoBackground(logoUrl);
      if (sampled) onChange(sampled);
      else setNote('The logo has no solid background colour to match.');
    } catch {
      setNote('Could not read the logo colours.');
    } finally {
      setMatching(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-[11px] text-slate-500">
        Background
        {logoUrl && (
          <button
            type="button"
            disabled={matching}
            onClick={() => void matchLogo()}
            className="flex items-center gap-1 text-[11px] text-teal-300 hover:text-teal-200 disabled:text-slate-500"
          >
            <Pipette className="w-3 h-3" />
            {matching ? 'Matching…' : 'Match logo background'}
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {swatches.map((swatch) => {
          const active = swatch === color.toLowerCase();
          return (
            <button
              key={swatch}
              type="button"
              title={swatch === brand ? 'Brand colour' : swatch}
              aria-label={swatch === brand ? 'Brand colour' : `Colour ${swatch}`}
              aria-pressed={active}
              onClick={() => onChange(swatch)}
              className={`h-7 w-7 rounded-md border ${active ? 'ring-2 ring-teal-400 border-transparent' : 'border-slate-600'}`}
              style={{ backgroundColor: swatch }}
            />
          );
        })}
        <input
          type="color"
          value={color}
          aria-label="Custom plate colour"
          title="Custom colour"
          onChange={(event) => onChange(event.target.value)}
          className="h-7 w-9 rounded border border-slate-700 bg-transparent"
        />
      </div>
      {note && <p className="text-[11px] text-amber-200">{note}</p>}
    </div>
  );
}

function BlankInspector({
  clip,
  start,
  state,
  agent,
  onEdit,
  onAgent,
  onUploadLogo,
  onPlayFrom,
  onSelect,
}: {
  clip: EditorClip;
  start: number;
  state: EditorState;
  onSelect: SidePanelProps['onSelect'];
  agent: AgentData;
  onEdit: SidePanelProps['onEdit'];
  onAgent: SidePanelProps['onAgent'];
  onUploadLogo: SidePanelProps['onUploadLogo'];
  onPlayFrom: SidePanelProps['onPlayFrom'];
}) {
  const color = plateColor(clip.background);
  const clips = state.clips;
  const first = clips[0];
  const last = clips[clips.length - 1];
  const isOpening = first?.id === clip.id;
  const isEnd = last?.id === clip.id && !isOpening;
  const openingPlate = first?.blank ? first : null;
  const endPlate = last?.blank && last.id !== first?.id ? last : null;
  const logoX = clip.logoX ?? 0.5;
  const logoY = clip.logoY ?? 0.5;
  const logoScale = clip.logoScale ?? DEFAULT_PLATE_LOGO_SCALE;
  return (
    <section className="space-y-3 border-t border-slate-800 pt-3">
      <p className="text-xs text-slate-400">{isOpening ? 'Opening plate' : isEnd ? 'End plate' : 'Colour plate'}</p>
      {isOpening && (
        <button
          type="button"
          onClick={() => {
            if (endPlate) {
              onEdit((current) => copyPlateStyle(current, clip.id, endPlate.id));
              return;
            }
            const added = insertBlank(state, 'end', { color: clip.background });
            const id = added.clips[added.clips.length - 1].id;
            onEdit(() => copyPlateStyle(added, clip.id, id));
            onSelect({ type: 'clip', id });
          }}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-100 hover:border-teal-400/70"
        >
          <Copy className="w-3.5 h-3.5 text-teal-300" />
          {endPlate ? 'Copy this to the end plate' : 'Add a matching end plate'}
        </button>
      )}
      {isEnd && openingPlate && (
        <button
          type="button"
          onClick={() => onEdit((current) => copyPlateStyle(current, openingPlate.id, clip.id))}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-100 hover:border-teal-400/70"
        >
          <Copy className="w-3.5 h-3.5 text-teal-300" />
          Copy the opening plate
        </button>
      )}
      <PlateBackground
        color={color}
        brandColor={agent.brand_color}
        logoUrl={clip.showLogo !== false ? agent.logo_url : undefined}
        onChange={(background) => onEdit((state) => updateBlank(state, clip.id, { background }))}
      />
      <label className="block text-[11px] text-slate-500">
        Duration (seconds)
        <input
          type="number"
          min={0.25}
          max={15}
          step={0.1}
          value={clip.duration}
          onChange={(event) => onEdit((state) => updateBlank(state, clip.id, { duration: Number(event.target.value) }))}
          className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm text-white"
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-slate-300">
        <input
          type="checkbox"
          checked={clip.showLogo !== false}
          onChange={(event) => onEdit((state) => updateBlank(state, clip.id, { showLogo: event.target.checked }))}
        />
        Show uploaded logo
      </label>
      {clip.showLogo !== false && (
        <>
          <MotionControls
            title="Logo animation"
            motion={plateLogoMotion(clip)}
            onChange={(logoMotion) => onEdit((state) => updateBlank(state, clip.id, { logoMotion }))}
            onPlay={() => onPlayFrom(start)}
            onTryOut={(seconds) => onPlayFrom(start, start + seconds + MOTION_PREVIEW_HOLD)}
          />
          <LogoSize
            value={logoScale}
            min={MIN_PLATE_LOGO_SCALE}
            max={MAX_PLATE_LOGO_SCALE}
            onChange={(next) => {
              const pos = respotLogo(logoX, logoY, logoScale, next);
              onEdit((current) => updateBlank(current, clip.id, { logoScale: next, logoX: pos.x, logoY: pos.y }));
            }}
          />
          <LogoPosition
            x={logoX}
            y={logoY}
            scale={logoScale}
            onChange={(pos) => onEdit((current) => updateBlank(current, clip.id, { logoX: pos.x, logoY: pos.y }))}
          />
          <LogoPicker agent={agent} onAgent={onAgent} onUploadLogo={onUploadLogo} swatch={color} />
        </>
      )}
    </section>
  );
}

const MOTION_ICONS: Record<MotionStyle, React.ComponentType<{ className?: string }>> = {
  none: Ban,
  fade: Blend,
  grow: Maximize2,
  rise: ArrowUpFromLine,
  drift: MoveRight,
  slide: ChevronsRight,
  bungee: ArrowDownToLine,
  tumble: RotateCw,
  spin: RefreshCw,
  wipe: PanelLeftOpen,
};

const DIRECTION_ICONS: Record<MotionDirection, React.ComponentType<{ className?: string }>> = {
  up: ArrowUp,
  down: ArrowDown,
  left: ArrowLeft,
  right: ArrowRight,
};

const MOTION_PREVIEW_HOLD = 0.4;

function MotionControls({
  title,
  motion,
  onChange,
  onPlay,
  onTryOut,
}: {
  title: string;
  motion: ElementMotion;
  onChange: (motion: ElementMotion) => void;
  onPlay: () => void;
  /** Replay just the entrance, `seconds` long, so a change is seen straight away. */
  onTryOut: (seconds: number) => void;
}) {
  const option = MOTION_STYLES.find((item) => item.value === motion.style) ?? MOTION_STYLES[0];
  const patch = (partial: Partial<ElementMotion>, tryOut = true) => {
    const next = { ...motion, ...partial };
    onChange(next);
    if (tryOut && next.style !== 'none') onTryOut(next.duration);
  };
  const pickStyle = (style: MotionStyle) => {
    const next = MOTION_STYLES.find((item) => item.value === style);
    const direction = next && next.directions.length > 0 && !next.directions.includes(motion.direction)
      ? next.directions[next.directions.length - 1]
      : motion.direction;
    patch({ style, direction });
  };
  const tryOutDuration = () => {
    if (motion.style !== 'none') onTryOut(motion.duration);
  };
  const showFade = motion.style !== 'fade' && motion.style !== 'wipe';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-500">{title}</p>
        {motion.style !== 'none' && (
          <button type="button" onClick={onPlay} className="flex items-center gap-1 text-[11px] text-teal-300 hover:text-teal-200">
            <Play className="w-3 h-3" />
            Preview
          </button>
        )}
      </div>
      <div className="grid grid-cols-5 gap-1">
        {MOTION_STYLES.map((item) => {
          const Icon = MOTION_ICONS[item.value];
          const active = item.value === motion.style;
          return (
            <button
              key={item.value}
              type="button"
              title={item.hint}
              aria-pressed={active}
              onClick={() => pickStyle(item.value)}
              className={`flex flex-col items-center gap-1 rounded-md border px-0.5 py-1.5 text-[10px] ${
                active ? 'border-teal-400 bg-teal-500/10 text-teal-100' : 'border-slate-700 text-slate-400 hover:border-slate-500'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {item.label}
            </button>
          );
        })}
      </div>
      {motion.style !== 'none' && (
        <div className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5">
          <label className="block text-[11px] text-slate-500">
            Duration
            <div className="mt-1 flex items-center gap-2">
              <input
                type="range"
                min={MIN_MOTION_SECONDS}
                max={MAX_MOTION_SECONDS}
                step={0.05}
                value={motion.duration}
                onChange={(e) => patch({ duration: Number(e.target.value) }, false)}
                onPointerUp={tryOutDuration}
                onKeyUp={tryOutDuration}
                className="flex-1 accent-teal-400"
                aria-label="Animation duration"
              />
              <input
                type="number"
                min={MIN_MOTION_SECONDS}
                max={MAX_MOTION_SECONDS}
                step={0.05}
                value={motion.duration}
                onChange={(e) => patch({ duration: Number(e.target.value) })}
                className="w-16 rounded-md bg-slate-900 border border-slate-700 px-1.5 py-1 text-xs text-white text-right"
                aria-label="Animation duration in seconds"
              />
              <span className="text-slate-500">s</span>
            </div>
          </label>
          <label className="block text-[11px] text-slate-500">
            Personality
            <select
              value={motion.personality}
              onChange={(e) => patch({ personality: e.target.value as MotionPersonality })}
              className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm text-white"
            >
              {MOTION_PERSONALITIES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
          {showFade && (
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={motion.fade} onChange={(e) => patch({ fade: e.target.checked })} />
              Fade
            </label>
          )}
          {option.directions.length > 0 && (
            <div>
              <p className="text-[11px] text-slate-500">Direction</p>
              <div className="mt-1 flex gap-1.5">
                {option.directions.map((direction) => {
                  const Icon = DIRECTION_ICONS[direction];
                  const active = motion.direction === direction;
                  return (
                    <button
                      key={direction}
                      type="button"
                      aria-label={`Move ${direction}`}
                      aria-pressed={active}
                      onClick={() => patch({ direction })}
                      className={`h-8 w-8 grid place-items-center rounded-md border ${
                        active ? 'border-teal-400 bg-teal-400 text-slate-950' : 'border-slate-700 text-slate-300 hover:border-slate-500'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {option.travels && (
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={motion.startOutside} onChange={(e) => patch({ startOutside: e.target.checked })} />
              Start outside the frame
            </label>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={motion.exit} onChange={(e) => patch({ exit: e.target.checked }, false)} />
            Animate out at the end
          </label>
        </div>
      )}
    </div>
  );
}

const SPEED_SECTION_SECONDS = 2;

function speedLabel(speed: number): string {
  return `${speed}×`;
}

/** Whole-clip speed, or slower and faster sections of the footage with eased changes between them. */
function SpeedControls({
  clip,
  sourceSeconds,
  clipStart,
  playhead,
  onEdit,
  onPlayFrom,
}: {
  clip: EditorClip;
  sourceSeconds: number | null;
  clipStart: number;
  playhead: number;
  onEdit: SidePanelProps['onEdit'];
  onPlayFrom: SidePanelProps['onPlayFrom'];
}) {
  const trim = clip.trimStart;
  const footage = Math.max(0, clipSourceEnd(clip) - trim);
  const zones = normaliseSpeedZones(clip.speedZones).filter((zone) => zone.end > trim && zone.start < trim + footage);
  const whole = zones.length === 1 && zones[0].start <= trim && zones[0].end >= trim + footage - 0.01 ? zones[0].speed : zones.length === 0 ? 1 : null;
  const shown = zones.map((zone) => ({
    from: roundTime(Math.max(0, zone.start - trim)),
    to: roundTime(Math.min(footage, zone.end - trim)),
    speed: zone.speed,
  }));

  const save = (next: Array<{ from: number; to: number; speed: number }>) =>
    onEdit((state) =>
      setClipSpeedZones(
        state,
        clip.id,
        next.map((section) => ({ start: trim + section.from, end: trim + section.to, speed: section.speed })),
        sourceSeconds,
      ),
    );

  const addSection = () => {
    const local = playhead - clipStart;
    const inside = local >= 0 && local < clip.duration;
    const at = inside ? speedSourceAt(clip.speedZones, trim, local) - trim : footage / 2 - SPEED_SECTION_SECONDS / 2;
    const from = roundTime(Math.max(0, Math.min(at, footage - MIN_SPEED_ZONE_SECONDS)));
    const to = roundTime(Math.min(footage, from + SPEED_SECTION_SECONDS));
    onEdit((state) => insertSpeedZone(state, clip.id, { start: trim + from, end: trim + to, speed: 0.5 }, sourceSeconds));
  };

  const watch = (section: { from: number; to: number }) => {
    const begin = speedOutputSeconds(clip.speedZones, trim, trim + section.from);
    const end = speedOutputSeconds(clip.speedZones, trim, trim + section.to);
    onPlayFrom(Math.max(clipStart, clipStart + begin - 1), Math.min(clipStart + clip.duration, clipStart + end + 1));
  };

  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-slate-200">
        <Gauge className="w-3.5 h-3.5 text-teal-300" />
        Speed
      </p>
      <div className="flex flex-wrap gap-1">
        {[0.5, 1, 1.5, 2, 3, 4, 6, 8].map((speed) => (
          <button
            key={speed}
            type="button"
            onClick={() => onEdit((state) => setClipSpeed(state, clip.id, speed, sourceSeconds))}
            className={`rounded-md px-2 py-1 text-[11px] border ${whole === speed ? 'border-teal-400 text-teal-100' : 'border-slate-700 text-slate-400'}`}
          >
            {speed === 1 ? 'Normal' : speedLabel(speed)}
          </button>
        ))}
      </div>
      {whole === null && <p className="text-[11px] text-slate-500">Parts of this clip play at different speeds.</p>}
      {shown.length > 0 && whole === null && (
        <ul className="space-y-1.5">
          {shown.map((section, index) => (
            <li key={index} className="rounded-md border border-slate-800 bg-slate-950/50 p-2 space-y-1.5">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                <label className="flex items-center gap-1">
                  From
                  <input
                    type="number"
                    min={0}
                    max={footage}
                    step={0.1}
                    value={section.from}
                    onChange={(e) => save(shown.map((s, i) => (i === index ? { ...s, from: Number(e.target.value) } : s)))}
                    className="w-14 rounded bg-slate-900 border border-slate-700 px-1 py-0.5 text-xs text-slate-100"
                    aria-label="Section starts at (seconds into the clip)"
                  />
                </label>
                <label className="flex items-center gap-1">
                  to
                  <input
                    type="number"
                    min={0}
                    max={footage}
                    step={0.1}
                    value={section.to}
                    onChange={(e) => save(shown.map((s, i) => (i === index ? { ...s, to: Number(e.target.value) } : s)))}
                    className="w-14 rounded bg-slate-900 border border-slate-700 px-1 py-0.5 text-xs text-slate-100"
                    aria-label="Section ends at (seconds into the clip)"
                  />
                  s
                </label>
                <button type="button" onClick={() => watch(section)} className="ml-auto p-1 text-slate-400 hover:text-teal-200" title="Watch this part">
                  <Play className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => save(shown.filter((_, i) => i !== index))} className="p-1 text-slate-400 hover:text-rose-300" title="Back to normal speed">
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {CLIP_SPEED_PRESETS.map((speed) => (
                  <button
                    key={speed}
                    type="button"
                    onClick={() => save(shown.map((s, i) => (i === index ? { ...s, speed } : s)))}
                    className={`rounded px-1.5 py-0.5 text-[10px] border ${section.speed === speed ? 'border-teal-400 text-teal-100' : 'border-slate-700 text-slate-400'}`}
                  >
                    {speedLabel(speed)}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={addSection}
        className="w-full flex items-center justify-center gap-1.5 rounded-md border border-dashed border-slate-700 py-1.5 text-[11px] text-slate-300 hover:border-teal-400 hover:text-teal-100"
      >
        <Plus className="w-3 h-3" />
        Slow down or speed up a part
      </button>
      <p className="text-[11px] text-slate-500">
        On the timeline, double-click the Speed row to add a ramp. Drag the coloured label sideways to move it, or up and
        down to change the speed. Drag the round dots to change where it starts and ends. Speed changes ease in and out,
        the clip gets longer or shorter to fit, and its own sound is muted wherever it isn't at normal speed.
      </p>
    </div>
  );
}

function Inspector(props: SidePanelProps) {
  const selection = props.selection;
  if (!selection) return null;
  if (selection.type === 'clip') {
    const clip = props.state.clips.find((item) => item.id === selection.id);
    if (!clip) return null;
    if (clip.blank) {
      const start = layOutClips(props.state.clips).clips.find((item) => item.id === clip.id)?.start ?? 0;
      return (
        <BlankInspector
          clip={clip}
          start={start}
          state={props.state}
          onSelect={props.onSelect}
          agent={props.agent}
          onEdit={props.onEdit}
          onAgent={props.onAgent}
          onUploadLogo={props.onUploadLogo}
          onPlayFrom={props.onPlayFrom}
        />
      );
    }
    const asset = props.assets.find((item) => item.id === clip.assetId);
    if (!asset) return null;
    const first = props.state.clips[0]?.id === clip.id;
    const animated = isAnimatedScene(clip);
    const photo = props.assets.find((item) => item.id === stillAssetId(clip));
    const label = animated ? photo?.original_filename : asset.original_filename;
    return (
      <section className="space-y-3 border-t border-slate-800 pt-3">
        <p className="text-xs text-slate-400 truncate">{label || 'Clip'}</p>
        <label className="block text-[11px] text-slate-500">
          Duration (seconds)
          <input
            type="number"
            min={0.25}
            step={0.1}
            value={clip.duration}
            onChange={(e) => props.onEdit((state) => setClipTrim(state, clip.id, clip.trimStart, Number(e.target.value), asset.duration_seconds, asset.kind))}
            className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-[11px] text-slate-500">
          Size · {Math.round(clipScale(clip) * 100)}%
          <input
            type="range"
            min={MIN_CLIP_SCALE}
            max={1}
            step={0.01}
            value={clipScale(clip)}
            onChange={(e) => props.onEdit((state) => setClipScale(state, clip.id, Number(e.target.value)))}
            className="w-full"
            aria-label="Clip size"
          />
        </label>
        {clipScale(clip) < 1 ? (
          <>
            <p className="text-[11px] text-slate-500">
              Shows the whole picture smaller, on the background. Drag it in the preview to move it, or drag its corner to resize.
            </p>
            <label className="block text-[11px] text-slate-500">
              Position · horizontal
              <input type="range" min={-(1 - clipScale(clip)) / 2} max={(1 - clipScale(clip)) / 2} step={0.01} value={clip.offsetX} onChange={(e) => props.onEdit((state) => setClipOffset(state, clip.id, Number(e.target.value), clip.offsetY))} className="w-full" />
            </label>
            <label className="block text-[11px] text-slate-500">
              Position · vertical
              <input type="range" min={-(1 - clipScale(clip)) / 2} max={(1 - clipScale(clip)) / 2} step={0.01} value={clip.offsetY} onChange={(e) => props.onEdit((state) => setClipOffset(state, clip.id, clip.offsetX, Number(e.target.value)))} className="w-full" />
            </label>
          </>
        ) : animated ? (
          <p className="text-[11px] text-slate-500">The animation was made for this frame. Go back to the photo to change its framing.</p>
        ) : (
          <>
            <div className="flex gap-2">
              {(['cover', 'contain'] as const).map((fit) => (
                <button
                  key={fit}
                  type="button"
                  onClick={() => props.onEdit((state) => setClipFit(state, clip.id, fit))}
                  className={`flex-1 rounded-md py-1.5 text-xs border ${clip.fit === fit ? 'border-teal-400 text-teal-100' : 'border-slate-700 text-slate-400'}`}
                >
                  {fit === 'cover' ? 'Crop to fill' : 'Fit with background'}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500">
              {clip.fit === 'cover'
                ? 'Fills the whole frame. Parts of the edges are cropped, never stretched.'
                : 'Shows the whole picture on a dark background, never stretched.'}
            </p>
            <label className="block text-[11px] text-slate-500">
              {clip.fit === 'cover' ? 'Crop position · horizontal' : 'Position · horizontal'}
              <input type="range" min={-1} max={1} step={0.05} value={clip.offsetX} onChange={(e) => props.onEdit((state) => setClipOffset(state, clip.id, Number(e.target.value), clip.offsetY))} className="w-full" />
            </label>
            <label className="block text-[11px] text-slate-500">
              {clip.fit === 'cover' ? 'Crop position · vertical' : 'Position · vertical'}
              <input type="range" min={-1} max={1} step={0.05} value={clip.offsetY} onChange={(e) => props.onEdit((state) => setClipOffset(state, clip.id, clip.offsetX, Number(e.target.value)))} className="w-full" />
            </label>
          </>
        )}
        {photo?.kind === 'photo' && <SceneMotionPanel clip={clip} photo={photo} tools={props.scenes} />}
        {asset.kind === 'video' && !animated && <FootageVersionSwitch clip={clip} tools={props.scenes} />}
        {clipGap(clip) > 0 && (
          <div className="rounded-md border border-dashed border-slate-700 px-2.5 py-2 text-[11px] text-slate-400">
            {clipGap(clip).toFixed(1)}s gap before this clip, shown as black. {!first && 'Transitions only play between joined clips.'}
            <button
              type="button"
              onClick={() => props.onEdit((state) => closeClipGap(state, clip.id))}
              className="mt-1.5 block w-full rounded-md border border-slate-600 py-1.5 text-xs text-slate-100 hover:border-teal-300"
            >
              Close gap
            </button>
          </div>
        )}
        {!first && clipGap(clip) === 0 && (
          <>
            <div className="grid grid-cols-2 gap-1.5">
              {CLIP_TRANSITIONS.map((style) => (
                <button
                  key={style.value}
                  type="button"
                  title={style.hint}
                  onClick={() => props.onEdit((state) => setClipTransition(state, clip.id, style.value, clip.transitionDuration || 0.5))}
                  className={`rounded-md py-1.5 text-xs border ${shownTransition(clip.transitionIn) === style.value ? 'border-teal-400 text-teal-100' : 'border-slate-700 text-slate-400'}`}
                >
                  {style.label}
                </button>
              ))}
            </div>
            {clip.transitionIn !== 'cut' && (
              <label className="block text-[11px] text-slate-500">
                Transition (seconds)
                <input
                  type="number"
                  min={0.1}
                  max={2}
                  step={0.1}
                  value={clip.transitionDuration}
                  onChange={(e) => props.onEdit((state) => setClipTransition(state, clip.id, shownTransition(clip.transitionIn), Number(e.target.value)))}
                  className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm"
                />
              </label>
            )}
            {clip.transitionIn !== 'cut' && (
              <button
                type="button"
                onClick={() => props.onEdit((state) => setClipTransition(state, clip.id, 'cut', 0))}
                className="w-full rounded-md border border-rose-500/50 py-1.5 text-xs text-rose-200 hover:border-rose-400 hover:bg-rose-500/10"
              >
                Remove transition
              </button>
            )}
          </>
        )}
        {asset.kind === 'video' && (
          <ClipGradePanel
            clip={clip}
            onEdit={props.onEdit}
            onLiveEdit={props.onLiveEdit}
            onSampleClip={props.onSampleClip}
            picking={props.neutralPickClipId === clip.id}
            onPick={props.onNeutralPick}
            note={props.gradeNote}
            onNote={props.onGradeNote}
          />
        )}
        {asset.kind === 'video' && (
          <SpeedControls
            clip={clip}
            sourceSeconds={asset.duration_seconds}
            clipStart={layOutClips(props.state.clips).clips.find((item) => item.id === clip.id)?.start ?? 0}
            playhead={props.playhead}
            onEdit={props.onEdit}
            onPlayFrom={props.onPlayFrom}
          />
        )}
        {asset.kind === 'video' && !animated && (
          <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5">
            <VolumeSlider
              label="Clip sound"
              value={clip.muted ? 0 : clip.volume}
              onChange={(volume) => props.onEdit((state) => setClipAudio(state, clip.id, volume, volume <= 0))}
            />
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={clip.muted}
                onChange={(e) => props.onEdit((state) => setClipAudio(state, clip.id, e.target.checked ? clip.volume : Math.max(clip.volume, 0.5), e.target.checked))}
              />
              Mute this clip
            </label>
          </div>
        )}
      </section>
    );
  }
  if (selection.type === 'text') {
    const text = props.state.texts.find((item) => item.id === selection.id);
    if (!text) return null;
    return (
      <TextInspector
        text={text}
        videoLength={visualDuration(props.state)}
        brandColor={props.agent.brand_color}
        onEdit={props.onEdit}
        onSelect={props.onSelect}
        onPlayFrom={props.onPlayFrom}
      />
    );
  }
  if (selection.type === 'logo') {
    return props.tab === 'logo' ? null : (
      <button type="button" onClick={() => props.onTab('logo')} className="w-full rounded-md border border-slate-700 px-3 py-2 text-xs text-teal-200">
        Edit the logo in the Logo tab
      </button>
    );
  }
  const audio = props.state.audios.find((item) => item.id === selection.id);
  if (!audio) return null;
  return <AudioInspector audio={audio} onEdit={props.onEdit} />;
}

function LogoSize({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (scale: number) => void;
}) {
  return (
    <label className="block text-[11px] text-slate-500">
      Logo size
      <input
        type="range"
        min={min}
        max={max}
        step={0.01}
        value={value}
        aria-label="Logo size"
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-1 w-full accent-teal-400"
      />
      <span className="mt-1 block text-slate-400">Click the logo in the preview and drag the white corner to resize it.</span>
    </label>
  );
}

const LOGO_SPOTS: Array<{ id: string; label: string; col: 0 | 1 | 2; row: 0 | 1 | 2 }> = [
  { id: 'tl', label: 'Top left', col: 0, row: 0 },
  { id: 'tc', label: 'Top centre', col: 1, row: 0 },
  { id: 'tr', label: 'Top right', col: 2, row: 0 },
  { id: 'ml', label: 'Middle left', col: 0, row: 1 },
  { id: 'mc', label: 'Centre', col: 1, row: 1 },
  { id: 'mr', label: 'Middle right', col: 2, row: 1 },
  { id: 'bl', label: 'Bottom left', col: 0, row: 2 },
  { id: 'bc', label: 'Bottom centre', col: 1, row: 2 },
  { id: 'br', label: 'Bottom right', col: 2, row: 2 },
];
const LOGO_MARGIN = 0.04;
const LOGO_ROWS = [0.1, 0.5, 0.9];

function logoSpot(col: number, row: number, scale: number): { x: number; y: number } {
  const half = scale / 2;
  const x = col === 0 ? LOGO_MARGIN + half : col === 2 ? 1 - LOGO_MARGIN - half : 0.5;
  return { x: Math.round(x * 1000) / 1000, y: LOGO_ROWS[row] };
}

function findLogoSpot(x: number, y: number, scale: number) {
  return LOGO_SPOTS.find((spot) => {
    const p = logoSpot(spot.col, spot.row, scale);
    return Math.abs(p.x - x) < 0.01 && Math.abs(p.y - y) < 0.01;
  });
}

/** Re-fits a logo sitting on a grid spot after its size changes, so it stays tucked into that spot. */
function respotLogo(x: number, y: number, oldScale: number, newScale: number): { x?: number; y?: number } {
  const spot = findLogoSpot(x, y, oldScale);
  return spot ? logoSpot(spot.col, spot.row, newScale) : {};
}

function LogoPosition({
  x,
  y,
  scale,
  onChange,
}: {
  x: number;
  y: number;
  scale: number;
  onChange: (pos: { x: number; y: number }) => void;
}) {
  const active = findLogoSpot(x, y, scale);
  return (
    <div className="space-y-2">
      <p className="text-[11px] text-slate-500">Position</p>
      <div className="grid grid-cols-3 gap-1 w-32 aspect-video rounded-md border border-slate-700 bg-slate-900 p-1">
        {LOGO_SPOTS.map((spot) => (
          <button
            key={spot.id}
            type="button"
            title={spot.label}
            aria-label={spot.label}
            aria-pressed={active?.id === spot.id}
            onClick={() => onChange(logoSpot(spot.col, spot.row, scale))}
            className={`rounded-sm ${active?.id === spot.id ? 'bg-teal-400' : 'bg-slate-700 hover:bg-slate-500'}`}
          />
        ))}
      </div>
      <label className="block text-[11px] text-slate-500">
        Left to right
        <input
          type="range"
          min={0}
          max={1}
          step={0.005}
          value={x}
          onChange={(e) => onChange({ x: Number(e.target.value), y })}
          className="mt-1 w-full accent-teal-400"
        />
      </label>
      <label className="block text-[11px] text-slate-500">
        Top to bottom
        <input
          type="range"
          min={0}
          max={1}
          step={0.005}
          value={y}
          onChange={(e) => onChange({ x, y: Number(e.target.value) })}
          className="mt-1 w-full accent-teal-400"
        />
      </label>
    </div>
  );
}

function LogoTab(props: SidePanelProps) {
  const { state, onEdit } = props;
  const logo = state.logo;
  const hasLogo = Boolean(props.agent.logo_url);
  const on = logo.visible && hasLogo;
  const activeSpot = findLogoSpot(logo.x, logo.y, logo.scale);
  const firstFootage = layOutClips(state.clips).clips.find((clip) => !clip.blank)?.start ?? 0;
  const setLogo = (patch: Partial<EditorState['logo']>) => onEdit((current) => updateLogo(current, patch));

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <p className="text-xs text-slate-300">Logo bug</p>
        <p className="text-[11px] text-slate-500">
          A small logo in the corner, shown over your photos and clips. It stays off the opening, middle and end plates.
        </p>
      </div>

      <LogoPicker agent={props.agent} onAgent={props.onAgent} onUploadLogo={props.onUploadLogo} />

      <label className={`flex items-center justify-between rounded-md border border-slate-800 px-3 py-2 text-xs ${hasLogo ? 'text-slate-200' : 'text-slate-500'}`}>
        Show on the video
        <input
          type="checkbox"
          disabled={!hasLogo}
          checked={on}
          onChange={(e) => {
            setLogo({ visible: e.target.checked });
            if (e.target.checked) props.onSelect({ type: 'logo' });
          }}
          className="accent-teal-400"
        />
      </label>
      {!hasLogo && <p className="text-[11px] text-slate-500">Upload a logo first.</p>}

      {on && (
        <>
          <LogoPosition x={logo.x} y={logo.y} scale={logo.scale} onChange={(pos) => setLogo(pos)} />

          <LogoSize
            value={logo.scale}
            min={MIN_OVERLAY_LOGO_SCALE}
            max={MAX_OVERLAY_LOGO_SCALE}
            onChange={(scale) => setLogo({ scale, ...respotLogo(logo.x, logo.y, logo.scale, scale) })}
          />

          <MotionControls
            title="Animation"
            motion={overlayMotion(logo.motion)}
            onChange={(motion) => setLogo({ motion })}
            onPlay={() => props.onPlayFrom(firstFootage)}
            onTryOut={(seconds) => props.onPlayFrom(firstFootage, firstFootage + seconds + MOTION_PREVIEW_HOLD)}
          />
        </>
      )}
    </section>
  );
}

const TEXT_SWATCHES = ['#ffffff', '#0f172a', '#f8e7c4', '#c9a227', '#2dd4bf', '#f87171'];

function splitColor(value: string | null): { hex: string; alpha: number } {
  if (!value) return { hex: '#000000', alpha: 0.45 };
  if (/^#[0-9a-fA-F]{6}$/.test(value)) return { hex: value, alpha: 1 };
  const m = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
  if (!m) return { hex: '#000000', alpha: 0.45 };
  const hex = `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
  return { hex, alpha: m[4] == null ? 1 : Number(m[4]) };
}

function joinColor(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.round(alpha * 100) / 100})`;
}

function ToggleChip({ active, label, title, onClick, children }: {
  active: boolean;
  label: string;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={title}
      onClick={onClick}
      className={`h-8 flex-1 grid place-items-center rounded-md border text-xs ${
        active ? 'border-teal-400 bg-teal-500/15 text-teal-100' : 'border-slate-700 text-slate-300 hover:border-slate-500'
      }`}
    >
      {children}
    </button>
  );
}

function TextInspector({
  text,
  videoLength,
  brandColor,
  onEdit,
  onSelect,
  onPlayFrom,
}: {
  text: EditorText;
  videoLength: number;
  brandColor?: string;
  onEdit: SidePanelProps['onEdit'];
  onSelect: SidePanelProps['onSelect'];
  onPlayFrom: SidePanelProps['onPlayFrom'];
}) {
  const patch = (partial: Partial<EditorText>) => onEdit((state) => updateText(state, text.id, partial));
  const look = resolveTextStyle(text);
  const bg = splitColor(text.background);
  const swatches = brandColor && /^#[0-9a-fA-F]{6}$/.test(brandColor) && !TEXT_SWATCHES.includes(brandColor.toLowerCase())
    ? [brandColor, ...TEXT_SWATCHES]
    : TEXT_SWATCHES;
  const input = 'mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm text-white';
  return (
    <section className="space-y-3 border-t border-slate-800 pt-3">
      <textarea
        value={text.text}
        onChange={(e) => patch({ text: e.target.value })}
        rows={3}
        className="w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm"
      />

      <div className="grid grid-cols-2 gap-2">
        <label className="block text-[11px] text-slate-500">
          Font
          <select value={look.fontFamily} onChange={(e) => patch({ fontFamily: e.target.value as TextFont })} className={input}>
            {TEXT_FONTS.map((font) => (
              <option key={font.value} value={font.value}>{font.label}</option>
            ))}
          </select>
        </label>
        <label className="block text-[11px] text-slate-500">
          Weight
          <select value={look.fontWeight} onChange={(e) => patch({ fontWeight: Number(e.target.value) })} className={input}>
            {TEXT_WEIGHTS.map((weight) => (
              <option key={weight.value} value={weight.value}>{weight.label}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="block text-[11px] text-slate-500">
        Size
        <div className="mt-1 flex items-center gap-2">
          <input type="range" min={16} max={160} step={1} value={text.fontSize} onChange={(e) => patch({ fontSize: Number(e.target.value) })} className="flex-1 accent-teal-400" aria-label="Text size" />
          <input type="number" min={16} max={160} value={text.fontSize} onChange={(e) => patch({ fontSize: Number(e.target.value) })} className="w-16 rounded-md bg-slate-900 border border-slate-700 px-1.5 py-1 text-xs text-white text-right" aria-label="Text size in points" />
        </div>
      </label>

      <div className="flex gap-1.5">
        <ToggleChip active={look.italic} label="Italic" title="Italic" onClick={() => patch({ italic: !look.italic })}>
          <Italic className="w-3.5 h-3.5" />
        </ToggleChip>
        <ToggleChip active={look.uppercase} label="Capitals" title="All capitals" onClick={() => patch({ uppercase: !look.uppercase })}>
          <CaseUpper className="w-4 h-4" />
        </ToggleChip>
        <ToggleChip active={look.shadow} label="Shadow" title="Drop shadow, easier to read over photos" onClick={() => patch({ shadow: !look.shadow })}>
          <span style={{ textShadow: TEXT_SHADOW }}>S</span>
        </ToggleChip>
        {(['left', 'center', 'right'] as const).map((align) => {
          const Icon = align === 'left' ? AlignLeft : align === 'right' ? AlignRight : AlignCenter;
          return (
            <ToggleChip key={align} active={text.align === align} label={`Align ${align}`} title={`Align ${align}`} onClick={() => patch({ align })}>
              <Icon className="w-3.5 h-3.5" />
            </ToggleChip>
          );
        })}
      </div>

      <div>
        <p className="text-[11px] text-slate-500">Colour</p>
        <div className="mt-1 flex items-center gap-1.5">
          {swatches.map((swatch) => (
            <button
              key={swatch}
              type="button"
              aria-label={`Text colour ${swatch}`}
              onClick={() => patch({ color: swatch })}
              className={`h-6 w-6 rounded-full border ${toHex(text.color).toLowerCase() === swatch.toLowerCase() ? 'border-teal-300 ring-2 ring-teal-400/50' : 'border-slate-600'}`}
              style={{ background: swatch }}
            />
          ))}
          <input type="color" value={toHex(text.color)} onChange={(e) => patch({ color: e.target.value })} className="h-7 w-9 bg-transparent" aria-label="Custom text colour" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="block text-[11px] text-slate-500">
          Letter spacing
          <input type="range" min={-0.05} max={0.5} step={0.01} value={look.letterSpacing} onChange={(e) => patch({ letterSpacing: Number(e.target.value) })} className="mt-1 w-full accent-teal-400" />
        </label>
        <label className="block text-[11px] text-slate-500">
          Line height
          <input type="range" min={0.9} max={2} step={0.05} value={look.lineHeight} onChange={(e) => patch({ lineHeight: Number(e.target.value) })} className="mt-1 w-full accent-teal-400" />
        </label>
        <label className="block text-[11px] text-slate-500">
          Box width
          <input type="range" min={MIN_TEXT_WIDTH} max={MAX_TEXT_WIDTH} step={0.01} value={look.width} onChange={(e) => patch({ width: Number(e.target.value) })} className="mt-1 w-full accent-teal-400" />
        </label>
        <label className="block text-[11px] text-slate-500">
          Opacity
          <input type="range" min={0.1} max={1} step={0.05} value={look.opacity} onChange={(e) => patch({ opacity: Number(e.target.value) })} className="mt-1 w-full accent-teal-400" />
        </label>
      </div>

      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={Boolean(text.background)}
            onChange={(e) => patch({ background: e.target.checked ? joinColor(bg.hex, bg.alpha) : null })}
          />
          Background box
        </label>
        {text.background && (
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={bg.hex}
              onChange={(e) => patch({ background: joinColor(e.target.value, bg.alpha) })}
              className="h-7 w-9 bg-transparent"
              aria-label="Background colour"
            />
            <input
              type="range"
              min={0.1}
              max={1}
              step={0.05}
              value={bg.alpha}
              onChange={(e) => patch({ background: joinColor(bg.hex, Number(e.target.value)) })}
              className="flex-1 accent-teal-400"
              aria-label="Background opacity"
            />
          </div>
        )}
      </div>

      <div>
        <p className="text-[11px] text-slate-500">Position</p>
        <div className="mt-1 grid grid-cols-3 gap-1.5">
          {([['Top', 0.15], ['Middle', 0.5], ['Bottom', 0.85]] as const).map(([label, y]) => (
            <button
              key={label}
              type="button"
              onClick={() => patch({ x: 0.5, y })}
              className={`rounded-md border py-1 text-xs ${Math.abs(text.y - y) < 0.01 && Math.abs(text.x - 0.5) < 0.01 ? 'border-teal-400 text-teal-100' : 'border-slate-700 text-slate-400'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <input type="range" min={0} max={1} step={0.01} value={text.x} onChange={(e) => patch({ x: Number(e.target.value) })} className="mt-2 w-full" aria-label="Horizontal position" />
        <input type="range" min={0} max={1} step={0.01} value={text.y} onChange={(e) => patch({ y: Number(e.target.value) })} className="w-full" aria-label="Vertical position" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-slate-500">
          Start
          <input type="number" min={0} step={0.1} value={text.start} onChange={(e) => patch({ start: Number(e.target.value) })} className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1 text-sm" />
        </label>
        <label className="text-[11px] text-slate-500">
          Duration
          <input type="number" min={0.25} step={0.1} value={text.duration} onChange={(e) => patch({ duration: Number(e.target.value) })} className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1 text-sm" />
        </label>
      </div>
      {videoLength > 0 && (
        <button type="button" className="text-xs text-teal-300 hover:text-teal-200" onClick={() => patch({ start: 0, duration: videoLength })}>
          Show for the whole video
        </button>
      )}

      <MotionControls
        title="Animation"
        motion={overlayMotion(text.motion)}
        onChange={(motion) => patch({ motion: overlayMotion(motion) })}
        onPlay={() => onPlayFrom(text.start)}
        onTryOut={(seconds) => onPlayFrom(text.start, text.start + seconds + MOTION_PREVIEW_HOLD)}
      />
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-slate-300 hover:text-white"
          onClick={() => {
            const id = createEditorId('text');
            onEdit((state) => addText(state, { ...text, id, y: Math.min(0.95, text.y + 0.08) }));
            onSelect({ type: 'text', id });
          }}
        >
          <Copy className="w-3.5 h-3.5" />
          Duplicate
        </button>
        <button
          type="button"
          className="text-xs text-red-300"
          onClick={() => {
            onEdit((state) => deleteText(state, text.id));
            onSelect(null);
          }}
        >
          Remove text
        </button>
      </div>
    </section>
  );
}

function AudioInspector({ audio, onEdit }: { audio: EditorAudio; onEdit: SidePanelProps['onEdit'] }) {
  const patch = (partial: Partial<EditorAudio>) => onEdit((state) => updateAudio(state, audio.id, partial));
  const voice = audio.role === 'voiceover';
  return (
    <section className="space-y-3 border-t border-slate-800 pt-3">
      <p className="text-xs text-slate-400">{voice ? 'Voiceover' : 'Music'}</p>
      {audio.provider && (
        <p className="text-[11px] text-amber-200">
          This track is no longer available. Remove it and choose a royalty-free track or upload your own.
        </p>
      )}
      <VolumeSlider label="Volume" value={audio.volume} onChange={(volume) => patch({ volume })} />
      <div className="grid grid-cols-2 gap-1.5">
        {(['music', 'voiceover'] as const).map((role) => (
          <button
            key={role}
            type="button"
            onClick={() => patch({ role })}
            className={`rounded-md py-1.5 text-xs border ${(audio.role ?? 'music') === role ? 'border-teal-400 text-teal-100' : 'border-slate-700 text-slate-400'}`}
          >
            {role === 'music' ? 'Music' : 'Voiceover'}
          </button>
        ))}
      </div>
      <label className="block text-[11px] text-slate-500">
        Start
        <input type="number" min={0} step={0.1} value={audio.start} onChange={(e) => patch({ start: Number(e.target.value) })} className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm" />
      </label>
      <label className="block text-[11px] text-slate-500">
        Trim in
        <input type="number" min={0} step={0.1} value={audio.trimStart} onChange={(e) => patch({ trimStart: Number(e.target.value) })} className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm" />
      </label>
      <label className="block text-[11px] text-slate-500">
        Duration
        <input type="number" min={0.25} step={0.1} value={audio.duration} onChange={(e) => patch({ duration: Number(e.target.value) })} className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-sm" />
      </label>
      <FadeSlider
        label="Fade in"
        value={audio.fadeIn}
        max={Math.min(MAX_AUDIO_FADE_SECONDS, audio.duration)}
        onChange={(fadeIn) => patch(clampAudioFades(fadeIn, Math.min(audio.fadeOut, audio.duration - fadeIn), audio.duration))}
      />
      <FadeSlider
        label="Fade out"
        value={audio.fadeOut}
        max={Math.min(MAX_AUDIO_FADE_SECONDS, audio.duration)}
        onChange={(fadeOut) => patch(clampAudioFades(Math.min(audio.fadeIn, audio.duration - fadeOut), fadeOut, audio.duration))}
      />
      <p className="text-[11px] text-slate-500">You can also drag the small handles at the top corners of the clip on the timeline.</p>
      <button type="button" className="text-xs text-red-300" onClick={() => onEdit((state) => deleteAudio(state, audio.id))}>
        Remove {voice ? 'voiceover' : 'audio'}
      </button>
    </section>
  );
}

function FadeSlider({ label, value, max, onChange }: { label: string; value: number; max: number; onChange: (seconds: number) => void }) {
  return (
    <label className="block text-[11px] text-slate-500">
      <span className="flex items-center justify-between">
        {label}
        <span className="text-slate-300 tabular-nums">{value > 0 ? `${value.toFixed(1)}s` : 'Off'}</span>
      </span>
      <input
        type="range"
        min={0}
        max={Math.max(0.1, max)}
        step={0.1}
        value={Math.min(value, max)}
        aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full accent-teal-400"
      />
    </label>
  );
}

function toHex(color: string): string {
  if (/^#[0-9a-fA-F]{6}$/.test(color)) return color;
  return '#ffffff';
}
